import { API_ENDPOINTS } from "../../enums/apiUrls";
import { logError } from "../../utils/customLogger";
import { getBearerToken } from "./bearerToken";

/** Astera primary-care case initiation form fields sent from the modal. */
export interface SubmitCaseInitiationPayload {
  mrn: string;
  dob: string;
  first_name: string;
  last_name: string;
  prescriber_name: string;
  drug: string;
  key: string;
  org_id: string;
}

/** Same pipeline as bulk Astera CSV: OncoEMR lookup then send-to-plan. */
const ASTERA_CASE_INIT_RPA_LIST = ["oncoemr", "send_to_plan"] as const;

/**
 * Maps case-initiation form data to the JSON body expected by RPA
 * ``/submit-pa`` (aligned with ``PharmacyPaRequest`` / bulk Astera rows).
 *
 * @param payload - Raw modal fields
 * @returns JSON body for RPA submit-pa
 */
function buildCaseInitiationRpaBody(
  payload: SubmitCaseInitiationPayload,
): Record<string, unknown> {
  const mrn = (payload.mrn || "").trim();
  const firstName = (payload.first_name || "").trim();
  const lastName = (payload.last_name || "").trim();
  const patientName = `${firstName} ${lastName}`.trim();

  return {
    patient_mrn: mrn.length > 0 ? mrn : null,
    drug_name: payload.drug.trim(),
    org_id: payload.org_id,
    patient_dob: payload.dob.trim(),
    patient_name: patientName.length > 0 ? patientName : null,
    patient_last_name: lastName.length > 0 ? lastName : null,
    prescriber_name: payload.prescriber_name.trim(),
    cmm_result_key: payload.key.trim(),
    rpa_list: [...ASTERA_CASE_INIT_RPA_LIST],
  };
}

/**
 * Parses RPA submit-pa response (JSON or plain text).
 *
 * @param text - Raw response body
 * @returns Parsed object or raw string
 */
function parseSubmitPaResponse(text: string): unknown {
  const trimmed = text.trim();
  if (!trimmed) {
    return null;
  }
  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    return trimmed;
  }
}

export const submitCaseInitiation = async (
  payload: SubmitCaseInitiationPayload,
): Promise<any> => {
  try {
    const token = await getBearerToken();

    if (!token) {
      throw new Error("No token found");
    }

    const body = buildCaseInitiationRpaBody(payload);

    const response = await fetch(API_ENDPOINTS.SUBMIT_CASE_INITIATION, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        accept: "application/json",
      },
      body: JSON.stringify(body),
    });

    const responseText = await response.text();

    if (response.status >= 400) {
      throw new Error(
        `Failed to submit case initiation: ${response.status} - ${responseText}`,
      );
    }

    const result = parseSubmitPaResponse(responseText);
    return {
      success: true,
      message: "Case initiated successfully",
      data: result,
    };
  } catch (error) {
    logError(error as Error, "Error in submitCaseInitiation");
    throw error;
  }
};
